import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Row,
  CardBody,
  Card,
  Container,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { FaWarehouse, FaUser, FaMoneyBillWave, FaBuilding, FaIdCard } from "react-icons/fa";

import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import { supplierActions } from "../../sagas/supplierSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { categoryActions } from "../../sagas/categorySlice";
import { productActions } from "../../sagas/productSlice";
import { formatPrice } from "../../helpers/utility";
import {
  CBL_PAGE,
  CPMT_INVOICES_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  CPMT_TRANSFER_PAGE,
  PAY_PAGE,
  RETOUR_PAGE,
} from "../../routes/routeConstants";

import POSHeader from "./POSHeader";
import ReceptionLines from "./ReceptionLines";
import POSKeypadModal from "./POSKeypadModal";
import SupplierSelectModal from "./SupplierSelectModal";

import "./POSPage.css";
import "./pos-reception-form.css";

const CReceiptNoteNew = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const reception = location.state?.reception;

  const { suppliers } = useSelector((s) => s.suppliers);
  const { entrepots } = useSelector((s) => s.entrepots);
  const {
    role,
    businessName,
    fullName,
    commercialId,
    pointOfSaleId,
  } = useSelector((state) => state.auth);

  const isAdmin = role === "ROLE_ADMIN";

  const [supplierModal, setSupplierModal] = useState(false);
  const [entrepotModal, setEntrepotModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadField, setKeypadField] = useState(null);
  const [keypadValue, setKeypadValue] = useState("");
  const [docKeypadOpen, setDocKeypadOpen] = useState(false);
  const [docKeypadValue, setDocKeypadValue] = useState("");
  const [activeTab, setActiveTab] = useState("RECEPTION");

  const [fournisseur, setFournisseur] = useState({
    nom: "",
    prenom: "",
    cin: "",
    telephone1: "",
    telephone2: "",
    email: "",
    adresse: "",
    ville: "Casablanca",
    typeFournisseur: "ENTREPRISE",
    codePostal: "",
    plafond: 0,
    entreprise: "",
    ice: "",
    active: true,
    solde: 0,
  });

  useEffect(() => {
    dispatch(supplierActions.search({ query: "" }));
    dispatch(entrepotActions.list());
    dispatch(categoryActions.list({ query: "" }));
    dispatch(productActions.findAllUnites());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      fournisseurId: reception?.fournisseurId || null,
      fournisseurNom: reception?.fournisseurNom || "",
      entrepotId: reception?.entrepotId || null,
      dateReception: reception?.dateReception || new Date().toISOString().split("T")[0],
      deliveryNoteNb: reception?.deliveryNoteNb || "",
      modeReglement: reception?.modeReglement || "ESPECES",
      lignes: reception?.lignes || [],
      montantTotalHT: reception?.montantTotalHT || 0,
      montantTotalTaxe: reception?.montantTotalTaxe || 0,
      montantTotalTTC: reception?.montantTotalTTC || 0,
      statut: reception?.statut || "EN_ATTENTE",
    },
    validationSchema: Yup.object({
      fournisseurId: Yup.number().required(),
      entrepotId: Yup.number().required(),
      lignes: Yup.array().min(1),
    }),
    onSubmit: (values) => {
      const payload = {
        data: values,
        onSuccess: () => {
          Swal.fire("OK", "Reception enregistree", "success");
          navigate(CPMT_RECEPTION_PAGE);
        },
      };

      if (reception) {
        dispatch(receiptNoteActions.updateReceiptNote({ ...payload, id: reception.id }));
      } else {
        dispatch(receiptNoteActions.createReceiptNote(payload));
      }
    },
  });

  const { values, setFieldValue, handleSubmit } = formik;
  const isReadOnly = values.statut === "VALIDE";

  const openSupplierFieldKeypad = (field) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setKeypadField(field);
    setKeypadValue(fournisseur[field] || "");
    setKeypadOpen(true);
  };

  const closeSupplierFieldKeypad = () => {
    setKeypadOpen(false);
    setKeypadField(null);
    setKeypadValue("");
  };

  const handleSupplierKeypadChange = (value) => {
    setKeypadValue(value);

    if (!keypadField) return;

    setFournisseur((prev) => ({
      ...prev,
      [keypadField]: value,
    }));

    setErrors((prev) => {
      if (!prev[keypadField]) return prev;
      const next = { ...prev };
      delete next[keypadField];
      return next;
    });
  };

  const handleSupplierSubmit = () => {
    const newErrors = {};

    if (fournisseur.typeFournisseur === "ENTREPRISE" && !fournisseur.entreprise) {
      newErrors.entreprise = "Entreprise obligatoire";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    dispatch(
      supplierActions.addSupplier({
        data: fournisseur,
        onSuccess: (created) => {
          setFieldValue("fournisseurId", created.id);
          setFieldValue("fournisseurNom", created.nom || created.entreprise);
          closeSupplierFieldKeypad();
          setShowSupplierModal(false);
          setErrors({});
          Swal.fire("Succes", "Fournisseur cree", "success");
        },
      })
    );
  };

  return (
    <Container fluid className="pos-page">
      <POSHeader
        businessName={businessName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        isAdmin={isAdmin}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "INVOICES") navigate(CPMT_INVOICES_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
      />

      <Form onSubmit={handleSubmit} className="pos-form">
        {isReadOnly && (
          <div className="pos-locked-banner">Reception validee - modification impossible</div>
        )}

        <Row className="pos-main-layout g-3 reception-pos-main">
          <Col md={8} className="pos-left">
            <Card className="shadow-sm rounded-4 border-0 h-100">
              <CardBody className="h-100 d-flex flex-column">
                

                <ReceptionLines formik={formik} readOnly={isReadOnly} mode="catalog" />
              </CardBody>
            </Card>
          </Col>

          <Col md={4} className="pos-right">
            <Card className="shadow-sm rounded-4 border-0 h-100 pos-cart-card reception-sidebar-card">
              <CardBody className="d-flex flex-column">
	                <div className="reception-sidebar-top p-3">
	                  <Row className="g-2 mt-1">
	                    <Col md={4}>
	                      <Label className="pos-label">Fournisseur</Label>
	                      <Button
	                        color="light"
	                        className="pos-select-btn w-100"
	                        onClick={() => !isReadOnly && setSupplierModal(true)}
	                        disabled={isReadOnly}
	                        type="button"
	                      >
	                        <FaUser className="me-2" />
	                        {values.fournisseurNom || "Choisir fournisseur"}
	                      </Button>
	                    </Col>

	                    <Col md={4}>
	                      <Label className="pos-label">Entrepot</Label>
	                      <Button
	                        color="light"
	                        className="pos-select-btn w-100"
	                        onClick={() => !isReadOnly && setEntrepotModal(true)}
	                        disabled={isReadOnly}
	                        type="button"
	                      >
	                        <FaWarehouse className="me-2" />
	                        {entrepots.find((e) => e.id === values.entrepotId)?.libelle ||
	                          "Choisir entrepot"}
	                      </Button>
	                    </Col>

	                    <Col md={4}>
	                      <Label className="pos-label">Date reception</Label>
	                      <Input
	                        type="date"
	                        value={values.dateReception}
	                        onChange={(e) => setFieldValue("dateReception", e.target.value)}
	                        className="pos-input"
	                        disabled={isReadOnly}
	                      />
	                    </Col>
	                  </Row>
	                </div>

                <div className="pos-cart-items reception-cart-items">
                  <ReceptionLines formik={formik} readOnly={isReadOnly} mode="cart" />
                </div>

                <div className="pos-cart-footer reception-cart-footer">
                  
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-bold fs-5">Total</span>
                    <span>{formatPrice(values.montantTotalTTC)}</span>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      color="secondary"
                      className="pos-action-btn flex-fill"
                      onClick={() => navigate(CPMT_RECEPTION_PAGE)}
                      type="button"
                    >
                      Annuler
                    </Button>

                    {!isReadOnly && (
                      <Button color="primary" type="submit" className="pos-action-btn flex-fill">
                        Enregistrer
                      </Button>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Form>

      <Modal isOpen={entrepotModal} toggle={() => setEntrepotModal(false)}>
        <ModalHeader>Choisir Entrepot</ModalHeader>
        <ModalBody>
          {entrepots.map((e) => (
            <Button
              key={e.id}
              block
              color="light"
              onClick={() => {
                setFieldValue("entrepotId", e.id);
                setEntrepotModal(false);
              }}
            >
              {e.code} - {e.libelle}
            </Button>
          ))}
        </ModalBody>
      </Modal>

      <POSKeypadModal
        isOpen={keypadOpen}
        toggle={closeSupplierFieldKeypad}
        value={keypadValue}
        onChange={handleSupplierKeypadChange}
        title="Saisie Fournisseur"
        showSearchButton={false}
      />

      <Modal
        isOpen={showSupplierModal}
        toggle={() => {
          closeSupplierFieldKeypad();
          setShowSupplierModal(false);
        }}
        size="xl"
        centered
        className="pos-client-modal"
      >
        <ModalHeader
          toggle={() => {
            closeSupplierFieldKeypad();
            setShowSupplierModal(false);
          }}
          className="border-0 pb-0"
        >
          <div className="d-flex flex-column">
            <div className="fw-bold fs-4">{t("supplier.new")}</div>
            <small className="text-muted">Ajoutez rapidement un fournisseur</small>
          </div>
        </ModalHeader>

        <ModalBody className="pt-2">
          <Card className="border-0 shadow-sm rounded-4">
            <CardBody>
              <Row>
                <Col md={6}>
                  <Label className="fw-bold">
                    <FaBuilding className="me-2 text-muted" />
                    Entreprise *
                  </Label>
                  <Input
                    name="entreprise"
                    value={fournisseur.entreprise}
                    readOnly
                    inputMode="none"
                    placeholder="Nom de l'entreprise"
                    invalid={!!errors.entreprise}
                    className="py-2 rounded-3 pos-nokeyboard"
                    onFocus={(e) => {
                      e.target.blur();
                      openSupplierFieldKeypad("entreprise");
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      openSupplierFieldKeypad("entreprise");
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      openSupplierFieldKeypad("entreprise");
                    }}
                    onClick={() => openSupplierFieldKeypad("entreprise")}
                  />
                  {errors.entreprise && (
                    <div className="text-danger small mt-1">{errors.entreprise}</div>
                  )}
                </Col>

                <Col md={6}>
                  <Label className="fw-bold">
                    <FaIdCard className="me-2 text-muted" />
                    ICE *
                  </Label>
                  <Input
                    name="ice"
                    value={fournisseur.ice}
                    readOnly
                    inputMode="none"
                    placeholder="Numero ICE"
                    invalid={!!errors.ice}
                    className="py-2 rounded-3 pos-nokeyboard"
                    onFocus={(e) => {
                      e.target.blur();
                      openSupplierFieldKeypad("ice");
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      openSupplierFieldKeypad("ice");
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      openSupplierFieldKeypad("ice");
                    }}
                    onClick={() => openSupplierFieldKeypad("ice")}
                  />
                  {errors.ice && <div className="text-danger small mt-1">{errors.ice}</div>}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="border-0 d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            outline
            className="fw-bold px-4 py-2 rounded-3"
            onClick={() => {
              closeSupplierFieldKeypad();
              setShowSupplierModal(false);
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            color="primary"
            className="fw-bold px-4 py-2 rounded-3"
            onClick={handleSupplierSubmit}
          >
            {t("common.save")}
          </Button>
        </ModalFooter>
      </Modal>

      <SupplierSelectModal
        isOpen={supplierModal}
        toggle={() => setSupplierModal(false)}
        suppliers={suppliers}
        selectedSupplier={suppliers.find((s) => s.id === values.fournisseurId)}
        onSelect={(s) => {
          if (!s) {
            setFieldValue("fournisseurId", null);
            setFieldValue("fournisseurNom", "");
            return;
          }

          setFieldValue("fournisseurId", s.id);
          setFieldValue("fournisseurNom", s.nom || s.entreprise);
        }}
        onAddNew={() => {
          setSupplierModal(false);
          setTimeout(() => setShowSupplierModal(true), 300);
        }}
      />

      <POSKeypadModal
        isOpen={docKeypadOpen}
        toggle={() => setDocKeypadOpen(false)}
        value={docKeypadValue}
        onChange={setDocKeypadValue}
        onSearch={() => {
          setFieldValue("deliveryNoteNb", docKeypadValue);
          setDocKeypadOpen(false);
        }}
        title="Numero Bon Livraison"
      />
    </Container>
  );
};

export default CReceiptNoteNew;
